/**
 * Sent Messages — admin view.
 *
 * Everything the club has sent: bulk emails staff wrote by hand, and the
 * school registration notifications the app sends on its own.
 *
 * The notifications are the reason this page exists. They could be switched
 * on per class, but nothing anywhere showed whether one had gone out, to
 * whom, or whether it arrived. Staff could turn the feature on and have no
 * way to answer a school asking why it had not been told about a player.
 *
 * Scoping happens on the server. An owner sees everything; an admin sees
 * their own classes plus whatever they sent themselves.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Mail, Send } from "lucide-react";
import toast from "react-hot-toast";

import DataTable from "../../components/admin/DataTable";
import FilterBar from "../../components/admin/FilterBar";
import Header from "../../components/Header";
import communicationLogsService from "../../api/services/communicationLogs.service";
import { formatDate } from "../../utils/format";

const PAGE_SIZE = 50;

const STATUS_STYLES = {
  sent: { label: "Sent", chip: "bg-green-100 text-green-800" },
  delivered: { label: "Delivered", chip: "bg-green-100 text-green-800" },
  pending: { label: "Queued", chip: "bg-amber-100 text-amber-800" },
  failed: { label: "Failed", chip: "bg-red-100 text-red-800" },
  bounced: { label: "Bounced", chip: "bg-red-100 text-red-800" },
};

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "sent", label: "Sent" },
  { value: "pending", label: "Queued" },
  { value: "failed", label: "Failed" },
  { value: "bounced", label: "Bounced" },
];

/**
 * A school notification is written by the app rather than by a person, and
 * is the one kind of message here nobody pressed send on.
 */
const isSchoolNotification = (row) => !row.sent_by_id && Boolean(row.class_id);

/**
 * A message staff sent goes out at once, and since Oct 2026 its row records
 * whether the provider took it. Rows from before then were left "pending"
 * with no outcome; they did go out, and must not read as still waiting.
 */
const UNTRACKED_SEND = {
  label: "Sent (not tracked)",
  chip: "bg-gray-100 text-gray-700",
};
const statusStyle = (value, row) =>
  value === "pending" && row.sent_by_id
    ? UNTRACKED_SEND
    : STATUS_STYLES[value] || { label: value, chip: "bg-gray-100 text-gray-700" };

export default function SentMessages() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await communicationLogsService.list({
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        status: statusFilter,
      });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (error) {
      console.error("Failed to load sent messages:", error);
      toast.error("Could not load sent messages. Please try again.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Changing the filter must go back to the first page, or an empty page 4
  // of the old result set is shown and reads as "nothing sent".
  const changeStatus = (value) => {
    setStatusFilter(value);
    setPage(0);
  };

  // Searching narrows the page in hand rather than the whole history: the
  // server has no text search on these and inventing one client-side that
  // silently only covers 50 rows would be worse than saying so.
  const filtered = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((row) =>
      [
        row.recipient_name,
        row.recipient_email,
        row.subject,
        row.class_name,
        row.sent_by_name,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle)),
    );
  }, [items, searchQuery]);

  const failedOnPage = useMemo(
    () =>
      items.filter((r) => r.status === "failed" || r.status === "bounced")
        .length,
    [items],
  );

  const columns = [
    {
      key: "recipient_name",
      label: "To",
      render: (value, row) => (
        <div className="min-w-0">
          <p className="font-semibold text-[#173151] truncate">
            {value || "Unknown"}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {row.recipient_email}
          </p>
        </div>
      ),
    },
    {
      key: "subject",
      label: "Message",
      render: (value, row) => (
        <div className="min-w-0 max-w-md">
          <p className="text-[#173151] truncate">{value || "(no subject)"}</p>
          {row.message_preview ? (
            <p className="text-xs text-gray-500 truncate">
              {row.message_preview}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: "sent_by_name",
      label: "Sent by",
      render: (value, row) =>
        isSchoolNotification(row) ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Send className="w-3 h-3" />
            School notice
          </span>
        ) : (
          <span className="text-gray-600 text-sm">{value || "System"}</span>
        ),
    },
    {
      key: "class_name",
      label: "Class",
      render: (value) => (
        <span className="text-gray-600 text-sm">{value || "—"}</span>
      ),
    },
    {
      key: "created_at",
      label: "When",
      sortable: true,
      render: (value, row) => (
        <span className="text-gray-600 text-sm whitespace-nowrap">
          {formatDate(row.sent_at || value)}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (value, row) => {
        const style = statusStyle(value, row);
        return (
          <div>
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${style.chip}`}
            >
              {style.label}
            </span>
            {row.error_message ? (
              <p
                className="text-xs text-red-600 mt-1 max-w-[220px] truncate"
                title={row.error_message}
              >
                {row.error_message}
              </p>
            ) : null}
          </div>
        );
      },
    },
  ];

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />

      <div className="max-w-9xl mx-auto px-3 sm:px-4 py-4 flex-1 flex flex-col min-h-0 w-full">
        <div className="shrink-0 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center items-start gap-3 sm:gap-4 justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-[30px] lg:text-[46px] font-bold text-text-primary font-kollektif truncate">
              Sent Messages
            </h1>
            <p className="text-xs sm:text-sm text-neutral-main font-manrope mt-1 hidden sm:block">
              Emails staff have sent, and the notices schools receive when a
              player registers
            </p>
          </div>

          <div className="flex gap-2 shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4 min-w-[80px] sm:min-w-[120px]">
              <p className="text-xs sm:text-sm text-gray-600 font-manrope">
                Total
              </p>
              <p className="text-lg sm:text-2xl font-bold text-[#173151] font-manrope mt-1">
                {total}
              </p>
            </div>
            {failedOnPage > 0 && (
              <div className="bg-white rounded-lg border border-red-200 p-2 sm:p-4 min-w-[80px] sm:min-w-[120px]">
                <p className="text-xs sm:text-sm text-red-600 font-manrope flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Failed
                </p>
                <p className="text-lg sm:text-2xl font-bold text-red-600 font-manrope mt-1">
                  {failedOnPage}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 flex flex-wrap items-center gap-2 mb-4">
          <Mail className="w-4 h-4 text-gray-400" />
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => changeStatus(option.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-manrope font-medium border transition-colors ${
                statusFilter === option.value
                  ? "bg-[#173151] text-white border-[#173151]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-[#173151]"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <FilterBar
          onSearch={setSearchQuery}
          searchValue={searchQuery}
          searchPlaceholder="Recipient, subject or class..."
          onClearFilters={() => setSearchQuery("")}
          hasActiveFilters={Boolean(searchQuery)}
        />

        <div className="flex-1 min-h-0 overflow-auto">
          <DataTable
            columns={columns}
            data={filtered}
            loading={loading}
            // Each server page is shown whole; the pager below moves
            // between server pages
            pagination={false}
            emptyMessage="Nothing has been sent yet."
          />
        </div>

        {lastPage > 0 && (
          <div className="shrink-0 flex items-center justify-between gap-4 pt-3">
            <p className="text-sm text-gray-600 font-manrope">
              Page {page + 1} of {lastPage + 1}
              {searchQuery ? " (search covers this page only)" : ""}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="px-4 py-1.5 rounded-lg border border-gray-300 text-sm font-manrope disabled:opacity-40 hover:border-[#173151]"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage || loading}
                className="px-4 py-1.5 rounded-lg border border-gray-300 text-sm font-manrope disabled:opacity-40 hover:border-[#173151]"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
